import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Transition as _Transition, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "panel-header draggable" }
const _hoisted_2 = { class: "panel-header__actions" }
const _hoisted_3 = {
  class: "drag-handle",
  title: "Drag to move"
}
const _hoisted_4 = { class: "panel-body" }
const _hoisted_5 = { class: "modal-body-content" }
const _hoisted_6 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_7 = {
  key: 1,
  class: "empty-notes"
}
const _hoisted_8 = { class: "empty-notes__inner" }
const _hoisted_9 = { class: "empty-icon-wrapper" }
const _hoisted_10 = {
  key: 0,
  class: "add-note-form"
}
const _hoisted_11 = { class: "form-group" }
const _hoisted_12 = { class: "add-note-actions" }
const _hoisted_13 = { class: "note-card-header" }
const _hoisted_14 = { class: "note-actions" }
const _hoisted_15 = ["title"]
const _hoisted_16 = { class: "btn-action__count" }
const _hoisted_17 = ["onClick", "disabled"]
const _hoisted_18 = ["onClick", "disabled"]
const _hoisted_19 = { class: "note-card-footer" }
const _hoisted_20 = { class: "meta-item" }
const _hoisted_21 = { class: "meta-text" }
const _hoisted_22 = { class: "meta-date-time" }
const _hoisted_23 = { class: "meta-date-text" }
const _hoisted_24 = { class: "meta-time-text" }
const _hoisted_25 = {
  key: 0,
  class: "panel-footer"
}

import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { v4 as uuidv4 } from "uuid";
import { Button, RichTextEditor, Icon } from "@/components/ui";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import SafeHtml from "@/components/ui/SafeHtml.vue";
import NotePreview from "@/components/ui/NotePreview.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import EditNoteModal from "@/components/note/EditNoteModal.vue";
import filters from "@/utils/filters";
import { useToast } from "@/composables/ui/useToast";
import { useFormDraft } from "@/composables/shared";
import {
  useNotes,
  useRegisterNote,
  useDeleteNote,
  useUploadDraftNoteAttachment,
  useDeleteNoteDraftAttachments,
} from "@/composables/notes";

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'NotesModal',
  props: {
    selectedContact: {},
    startInAddMode: { type: Boolean, default: false }
  },
  emits: ["closeNotesModal"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Emits
const emit = __emit;

// Store (only for account)
const store = useStore();
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Notes query
const notesParams = computed(() => {
  if (!props.selectedContact?.id || !organizationId.value) {
    return null;
  }
  return {
    contactId: props.selectedContact.id,
    organizationId: organizationId.value,
  };
});

const { data: notesData, isLoading: notesIsLoading, refetch: refetchNotes } = useNotes(notesParams);

// Register note mutation
const registerNoteMutation = useRegisterNote();
const isLoading = computed(() => registerNoteMutation.isPending.value);

// Delete note mutation
const { deleteNote: deleteNoteMutation, isDeleting: isDeletingNote } = useDeleteNote();

// Computed
const notes = computed(() => notesData.value || []);

// State
const showModal = ref(true);
const showEditNoteModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const selectedNote = ref<any>(null);
const showAddNoteForm = ref(false);
const registerNoteObj = ref({
  message: null as string | null,
  contactId: null as string | null,
  organizationId: null as string | null,
});

const notesToolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image", "attach-file"],
  ["clean"],
];

const draftKey = ref<string | null>(null);
const pendingUploads = ref(0);
const showCloseConfirm = ref(false);
const toast = useToast();
let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

const uploadDraft = useUploadDraftNoteAttachment();
const discardDraft = useDeleteNoteDraftAttachments();

// Shared with NextTaskFollowupModal.vue's "Add Note" field — both are just
// different entry points for jotting an unsaved note about the same contact,
// so they read/write the same draft under a common contact-scoped key.
const { loadDraft: loadNoteTextDraft, clearDraft: clearNoteTextDraft } = useFormDraft(
  registerNoteObj,
  {
    key: `contact-note-draft:${props.selectedContact?.id}`,
    serialize: (v) => ({ text: v.message ?? "" }),
    isEmpty: (v) => !v.text?.trim(),
  }
);

const handleEditorUpload = async (payload: {
  file: File;
  kind: "image" | "video" | "file";
  placeholderId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
  onSuccess?: (url: string) => void;
  onError?: (msg?: string) => void;
}) => {
  try {
    if (!draftKey.value) draftKey.value = uuidv4();
    const result = await uploadDraft.mutateAsync({
      draftKey: draftKey.value,
      files: [payload.file],
      onProgress: payload.onProgress,
      signal: payload.signal,
    });
    const uploaded = result?.[0];
    if (uploaded?.url && payload.onSuccess) {
      payload.onSuccess(uploaded.url);
    } else {
      payload.onError?.("Upload failed");
    }
  } catch (err: any) {
    if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
    payload.onError?.(err?.message || "Upload failed");
  }
};

watch(pendingUploads, (n) => {
  if (n > 0 && !beforeUnloadHandler) {
    beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Uploads in progress. Leave anyway?";
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
  } else if (n === 0 && beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
});

watch(showAddNoteForm, (open) => {
  if (open) {
    if (!draftKey.value) draftKey.value = uuidv4();
    if (!registerNoteObj.value.message?.trim()) {
      const draft = loadNoteTextDraft();
      if (draft?.text) registerNoteObj.value.message = draft.text;
    }
  }
});

// Template ref
const noteEditor = ref<any>(null);

// Drag state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};
  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
  };
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".panel-body",
    ".panel-footer",
    "button",
    "a",
    "input",
    "textarea",
    ".ql-toolbar",
    ".ql-editor",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  document.addEventListener("mousemove", handleDrag, { passive: false });
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag, { passive: false });
  document.addEventListener("touchend", stopDrag);
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

// Methods
const setValues = () => {
  registerNoteObj.value.contactId = props.selectedContact?.id ?? null;
  registerNoteObj.value.organizationId = account.value?.organizationId;
};

const closeModal = () => {
  if (noteEditor.value?.hasPendingUploads?.()) {
    showCloseConfirm.value = true;
    return;
  }
  if (!isLoading.value) cleanupDraftIfAny();
  emit("closeNotesModal");
};

const confirmCloseAndDiscard = () => {
  showCloseConfirm.value = false;
  noteEditor.value?.cancelAllUploads?.();
  if (!isLoading.value) cleanupDraftIfAny();
  emit("closeNotesModal");
};

const cleanupDraftIfAny = () => {
  const key = draftKey.value;
  if (!key) return;
  draftKey.value = null;
  discardDraft.mutate(key, { onError: () => {} });
};

const cancelAddNote = () => {
  noteEditor.value?.cancelAllUploads?.();
  showAddNoteForm.value = false;
  registerNoteObj.value.message = null;
  if (noteEditor.value && noteEditor.value.clear) {
    noteEditor.value.clear();
  }
  cleanupDraftIfAny();
  clearNoteTextDraft();
};

const selectNoteToEdit = (note: any) => {
  selectedNote.value = note;
  showEditNoteModal.value = true;
};

const countAttachments = (html: string) => {
  if (!html || typeof html !== "string") return 0;
  const imgs = (html.match(/<img\b/gi) || []).length;
  const vids = (html.match(/<video\b/gi) || []).length;
  const auds = (html.match(/<audio\b/gi) || []).length;
  const iframes = (html.match(/<iframe\b/gi) || []).length;
  const chips = (html.match(/class="[^"]*\brte-file-chip\b[^_]/gi) || []).length;
  return imgs + vids + auds + iframes + chips;
};

const attachmentCounts = computed(() => {
  const map = new Map<string, number>();
  (notes.value || []).forEach((note: any) => {
    map.set(note.id, countAttachments(note.message));
  });
  return map;
});

const attachmentCount = (noteId: string) => attachmentCounts.value.get(noteId) || 0;

const parseNote = (text: string) => {
  if (!text || typeof text !== "string") return "";

  const isHtmlEscaped = (html: string) => {
    if (!html || typeof html !== "string") return false;
    return /&lt;/.test(html) && !/</.test(html);
  };

  const decodeIfEscaped = (html: string) => {
    if (!html || typeof html !== "string") return html || "";
    if (!isHtmlEscaped(html)) {
      return html;
    }
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.innerHTML;
  };

  let decodedText = decodeIfEscaped(text);
  const hasHtmlTags = /<[^>]+>/g.test(decodedText);

  if (hasHtmlTags) {
    return decodedText;
  } else {
    // Plain text: convert URLs to links and newlines to <br>
    return decodedText
      .replace(/\n/g, "<br>")
      .replace(
        /(https?:\/\/[^\s]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #2d60ff; text-decoration: underline;">$1</a>'
      );
  }
};

const getDateOnly = (date: string) => {
  return filters.getDateOnly(date) || new Date(date).toLocaleDateString();
};

const getTimeOnly = (date: string) => {
  return filters.getTimeOnly(date) || new Date(date).toLocaleTimeString();
};

const registerNote = async () => {
  if (pendingUploads.value > 0) {
    toast.warning("Please wait for uploads to finish.");
    return;
  }
  try {
    if (noteEditor.value?.stripAllPlaceholders) {
      noteEditor.value.stripAllPlaceholders();
      const fresh = noteEditor.value?.getHTML?.();
      if (typeof fresh === "string") registerNoteObj.value.message = fresh;
    }
    const { message, contactId, organizationId } = registerNoteObj.value;
    const result = await registerNoteMutation.mutateAsync({
      message: message ?? "",
      contactId: contactId ?? "",
      organizationId: organizationId != null ? organizationId : undefined,
      ...(draftKey.value ? { draftKey: draftKey.value } : {}),
    });

    if (result) {
      if (noteEditor.value && noteEditor.value.clear) {
        noteEditor.value.clear();
      }
      registerNoteObj.value.message = null;
      showAddNoteForm.value = false;
      draftKey.value = null;
      clearNoteTextDraft();
      refetchNotes();
    }
  } catch (error) {
    console.error("Error registering note:", error);
  }
};

const toggleDeleteConfirmationModal = (note: any) => {
  selectedNote.value = note;
  showDeleteConfirmationModal.value = true;
};

const deleteNote = async () => {
  if (!selectedNote.value?.id) return;
  return deleteNoteMutation(selectedNote.value.id);
};

onMounted(() => {
  setValues();
  if (props.startInAddMode) showAddNoteForm.value = true;
});

onBeforeUnmount(() => {
  noteEditor.value?.cancelAllUploads?.();
  if (beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
  if (!isLoading.value) cleanupDraftIfAny();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_Transition, { name: "modal-fade" }, {
      default: _withCtx(() => [
        (showModal.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["draggable-overlay", { 'dragging-active': !isInitialPosition.value }])
            }, [
              _createElementVNode("div", {
                ref_key: "modalRef",
                ref: modalRef,
                class: _normalizeClass(["draggable-panel", { 'is-dragging': isDragging.value }]),
                style: _normalizeStyle(modalStyle.value),
                onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"])),
                onMousedown: startDrag,
                onTouchstart: startDrag
              }, [
                _createElementVNode("div", _hoisted_1, [
                  _cache[10] || (_cache[10] = _createElementVNode("div", { class: "panel-header__text" }, [
                    _createElementVNode("h2", { class: "panel-header__title" }, "Note History"),
                    _createElementVNode("p", { class: "panel-header__subtitle" }, "View and manage contact notes")
                  ], -1)),
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:drag",
                        width: "16",
                        height: "16"
                      })
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "panel-header__close",
                      onClick: closeModal,
                      "aria-label": "Close modal"
                    }, [
                      _createVNode(_unref(Icon), { icon: "mdi:close" })
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    (_unref(notesIsLoading))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createVNode(LoadingBars, { size: "sm" })
                        ]))
                      : (!_unref(notesIsLoading) && (!notes.value || notes.value.length === 0) && !showAddNoteForm.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                            _createElementVNode("div", _hoisted_8, [
                              _createElementVNode("div", _hoisted_9, [
                                _createVNode(_unref(Icon), {
                                  icon: "mdi:note-plus-outline",
                                  width: "28",
                                  height: "28",
                                  class: "empty-icon"
                                })
                              ]),
                              _cache[12] || (_cache[12] = _createElementVNode("div", { class: "empty-text" }, [
                                _createElementVNode("p", { class: "empty-title" }, "No notes yet"),
                                _createElementVNode("p", { class: "empty-description" }, " Add notes to track conversations and details for this contact. ")
                              ], -1)),
                              _createVNode(_unref(Button), {
                                variant: "primary",
                                icon: "mdi:plus",
                                onClick: _cache[0] || (_cache[0] = ($event: any) => (showAddNoteForm.value = true))
                              }, {
                                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                  _createTextVNode(" Add note ", -1)
                                ]))]),
                                _: 1
                              })
                            ])
                          ]))
                        : (!_unref(notesIsLoading))
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              (showAddNoteForm.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                    _createElementVNode("div", _hoisted_11, [
                                      _cache[13] || (_cache[13] = _createElementVNode("label", { class: "add-note-label" }, "Note", -1)),
                                      _createVNode(_unref(RichTextEditor), {
                                        ref_key: "noteEditor",
                                        ref: noteEditor,
                                        modelValue: registerNoteObj.value.message,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((registerNoteObj.value.message) = $event)),
                                        height: "150px",
                                        toolbar: notesToolbar,
                                        "accepts-uploads": true,
                                        onUpload: handleEditorUpload,
                                        onUploadsPending: _cache[2] || (_cache[2] = ($event: any) => (pendingUploads.value = $event))
                                      }, null, 8, ["modelValue"])
                                    ]),
                                    _createElementVNode("div", _hoisted_12, [
                                      _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        onClick: cancelAddNote
                                      }, {
                                        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                          _createTextVNode(" Cancel ", -1)
                                        ]))]),
                                        _: 1
                                      }),
                                      _createVNode(_unref(Button), {
                                        variant: "primary",
                                        onClick: registerNote,
                                        loading: isLoading.value && !_unref(isDeletingNote),
                                        disabled: !registerNoteObj.value.message?.trim() || pendingUploads.value > 0
                                      }, {
                                        default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                          _createTextVNode(" Save ", -1)
                                        ]))]),
                                        _: 1
                                      }, 8, ["loading", "disabled"])
                                    ])
                                  ]))
                                : _createCommentVNode("", true),
                              (notes.value && notes.value.length > 0)
                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(notes.value, (note, index) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                      key: index,
                                      class: "note-card"
                                    }, [
                                      _createElementVNode("div", _hoisted_13, [
                                        _createVNode(NotePreview, {
                                          class: "note-message",
                                          html: parseNote(note.message)
                                        }, null, 8, ["html"]),
                                        _createElementVNode("div", _hoisted_14, [
                                          (attachmentCount(note.id) > 0)
                                            ? (_openBlock(), _createElementBlock("span", {
                                                key: 0,
                                                class: "btn-action btn-attachments",
                                                title: `${attachmentCount(note.id)} attachment${attachmentCount(note.id) === 1 ? '' : 's'}`
                                              }, [
                                                _createVNode(_unref(Icon), { icon: "mdi:paperclip" }),
                                                _createElementVNode("span", _hoisted_16, _toDisplayString(attachmentCount(note.id)), 1)
                                              ], 8, _hoisted_15))
                                            : _createCommentVNode("", true),
                                          _createElementVNode("button", {
                                            onClick: ($event: any) => (selectNoteToEdit(note)),
                                            class: "btn-action btn-edit",
                                            disabled: isLoading.value || _unref(isDeletingNote),
                                            title: "Edit note"
                                          }, [
                                            _createVNode(_unref(Icon), { icon: "mdi:pencil-outline" })
                                          ], 8, _hoisted_17),
                                          _createElementVNode("button", {
                                            onClick: ($event: any) => (toggleDeleteConfirmationModal(note)),
                                            class: "btn-action btn-delete",
                                            disabled: isLoading.value,
                                            title: "Delete note"
                                          }, [
                                            _createVNode(_unref(Icon), { icon: "mdi:delete-outline" })
                                          ], 8, _hoisted_18)
                                        ])
                                      ]),
                                      _createElementVNode("div", _hoisted_19, [
                                        _createElementVNode("div", _hoisted_20, [
                                          _createVNode(_unref(Icon), {
                                            icon: "octicon:person-16",
                                            class: "meta-icon"
                                          }),
                                          _createElementVNode("span", _hoisted_21, _toDisplayString(note.createdBy.fullName), 1)
                                        ]),
                                        _createElementVNode("div", _hoisted_22, [
                                          _createElementVNode("span", _hoisted_23, _toDisplayString(getDateOnly(note.createdAt)), 1),
                                          _createElementVNode("span", _hoisted_24, _toDisplayString(getTimeOnly(note.createdAt)), 1)
                                        ])
                                      ])
                                    ]))
                                  }), 128))
                                : _createCommentVNode("", true)
                            ], 64))
                          : _createCommentVNode("", true)
                  ])
                ]),
                (!showAddNoteForm.value && notes.value && notes.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                      _createVNode(_unref(Button), {
                        variant: "primary",
                        block: "",
                        icon: "mdi:plus",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (showAddNoteForm.value = true))
                      }, {
                        default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                          _createTextVNode(" Add Note ", -1)
                        ]))]),
                        _: 1
                      })
                    ]))
                  : _createCommentVNode("", true)
              ], 38)
            ], 2))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (showEditNoteModal.value)
      ? (_openBlock(), _createBlock(EditNoteModal, {
          key: 0,
          selectedNote: selectedNote.value,
          onCloseEditNoteModal: _cache[5] || (_cache[5] = ($event: any) => (showEditNoteModal.value = false)),
          onRefreshNotes: _cache[6] || (_cache[6] = ($event: any) => (_unref(refetchNotes)()))
        }, null, 8, ["selectedNote"]))
      : _createCommentVNode("", true),
    (showDeleteConfirmationModal.value)
      ? (_openBlock(), _createBlock(DeleteConfirmationModal, {
          key: 1,
          deleteFunction: deleteNote,
          entity: "Note",
          plural: false,
          onCloseDeleteConfirmationModal: _cache[7] || (_cache[7] = ($event: any) => (showDeleteConfirmationModal.value = false)),
          onRefreshRecords: _cache[8] || (_cache[8] = ($event: any) => (_unref(refetchNotes)()))
        }))
      : _createCommentVNode("", true),
    _createVNode(ActionConfirmationModal, {
      show: showCloseConfirm.value,
      title: "Discard upload?",
      message: "Uploads are still in progress. Closing now will cancel them and discard the draft attachments.",
      "confirm-text": "Discard",
      "cancel-text": "Keep open",
      onClose: _cache[9] || (_cache[9] = ($event: any) => (showCloseConfirm.value = false)),
      onConfirm: confirmCloseAndDiscard
    }, null, 8, ["show"])
  ]))
}
}

})